import { isDefined } from "@clipboard-health/util-ts";

import { useSearchParams } from "../../../lib/utils/useSearchParams";
import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import { PlacementApplicationStatus } from "../types/fetchPlacements.schema";

interface UseEasySchedulingStateParams {
  placementDetails: PlacementWithDetails | undefined;
  interviewAvailability: unknown[];
}

interface UseEasySchedulingStateResult {
  isEasyScheduling: boolean;
  shouldFetchInterviewAvailability: boolean;
  shouldAutoOpenBookingModal: boolean;
  isEasySchedulingDeepLink: boolean;
}

export function useEasySchedulingState(
  params: UseEasySchedulingStateParams
): UseEasySchedulingStateResult {
  const { placementDetails, interviewAvailability } = params;

  const searchParams = useSearchParams<Record<string, string>>();
  const schedulingType = searchParams["scheduling-type"];
  const { requestEasySchedulingInterview } = searchParams;

  const hasEasySchedulingParameter =
    schedulingType === "easy-scheduling" || requestEasySchedulingInterview === "true";

  const isEasyScheduling = placementDetails?.isEasyScheduling ?? hasEasySchedulingParameter;
  const isEasySchedulingDeepLink = hasEasySchedulingParameter;

  const shouldFetchInterviewAvailability =
    isDefined(placementDetails?.workplace.id) &&
    ((placementDetails?.interviewSettings?.attributes?.scheduleInterviews ?? false) ||
      isEasyScheduling);

  const acceptedApplication = placementDetails?.applications?.find(
    (application) => application.attributes.status === PlacementApplicationStatus.ACCEPTED
  );
  const hasAcceptedApplication = acceptedApplication !== undefined;
  const hasBookedInterview = (placementDetails?.interviews?.length ?? 0) > 0;
  const hasInterviewAvailability = interviewAvailability.length > 0;

  const shouldAutoOpenBookingModal =
    isEasySchedulingDeepLink &&
    hasAcceptedApplication &&
    !hasBookedInterview &&
    hasInterviewAvailability;

  return {
    isEasyScheduling,
    shouldFetchInterviewAvailability,
    shouldAutoOpenBookingModal,
    isEasySchedulingDeepLink,
  };
}
